import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ComponentSize } from '../types';

export interface ListItem {
  id: any;
  label: string;
  description?: string;
  icon?: string;
  disabled?: boolean;
  selected?: boolean;
}

@Component({
  selector: 'app-list',
  imports: [CommonModule],
  template: `
    <div [class]="listClasses" [attr.role]="selectable() ? 'listbox' : 'list'">
      @for (item of items(); track item.id) {
        <div
          [class]="getItemClasses(item)"
          [attr.aria-selected]="selectable() ? item.selected : null"
          [attr.aria-disabled]="item.disabled"
          (click)="onItemClick(item)">
          @if (item.icon) {
            <div class="flex-shrink-0">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon" />
              </svg>
            </div>
          }
          <div class="flex-1 min-w-0">
            <div class="font-medium text-gray-900 dark:text-gray-100">{{ item.label }}</div>
            @if (item.description) {
              <div class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ item.description }}</div>
            }
          </div>
          @if (selectable() && item.selected) {
            <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          }
        </div>
      }
    </div>
  `,
  styles: []
})
export class ListComponent {
  items = input<ListItem[]>([]);
  selectable = input<boolean>(false);
  size = input<ComponentSize>('md');
  variant = input<'default' | 'bordered' | 'striped'>('default');

  itemClickEvent = output<ListItem>();

  onItemClick(item: ListItem): void {
    if (!item.disabled) {
      this.itemClickEvent.emit(item);
    }
  }

  get listClasses(): string {
    const classes = ['divide-y', 'divide-gray-200', 'dark:divide-gray-700'];
    
    if (this.variant() === 'bordered') {
      classes.push('border', 'border-gray-200', 'dark:border-gray-700', 'rounded-lg', 'overflow-hidden');
    }
    
    return classes.join(' ');
  }

  getItemClasses(item: ListItem): string {
    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
      xl: 'px-6 py-5 text-xl'
    };

    const classes = [
      'flex',
      'items-center',
      'gap-3',
      'transition-colors',
      'duration-150',
      sizeMap[this.size()]
    ];

    if (this.variant() === 'striped') {
      classes.push('odd:bg-gray-50', 'dark:odd:bg-gray-800/50');
    }

    if (item.disabled) {
      classes.push('opacity-50', 'cursor-not-allowed');
    } else if (this.selectable()) {
      classes.push('cursor-pointer', 'hover:bg-gray-50', 'dark:hover:bg-gray-700');
    }

    if (item.selected) {
      classes.push('bg-blue-50', 'dark:bg-blue-900/20', 'text-blue-900', 'dark:text-blue-100');
    } else {
      classes.push('text-gray-900', 'dark:text-gray-100');
    }

    return classes.join(' ');
  }
}

